/**
 * Phone Number Utility
 * Validates, normalizes and formats phone numbers to international format
 */

// Country codes with their local prefixes
const COUNTRY_CODES = {
  'DE': { code: '+49', localPrefix: '0', name: 'Deutschland', mobilePattern: /^(15|16|17)\d{8,9}$/ },
  'AT': { code: '+43', localPrefix: '0', name: 'Österreich', mobilePattern: /^(650|660|664|676|680|681|688|699)\d{6,8}$/ },
  'CH': { code: '+41', localPrefix: '0', name: 'Schweiz', mobilePattern: /^(76|77|78|79)\d{7}$/ },
  'FR': { code: '+33', localPrefix: '0', name: 'Frankreich', mobilePattern: /^[67]\d{8}$/ },
  'IT': { code: '+39', localPrefix: '', name: 'Italien', mobilePattern: /^3\d{8,9}$/ },
  'NL': { code: '+31', localPrefix: '0', name: 'Niederlande', mobilePattern: /^6\d{8}$/ },
  'BE': { code: '+32', localPrefix: '0', name: 'Belgien', mobilePattern: /^4\d{8}$/ },
  'PL': { code: '+48', localPrefix: '', name: 'Polen', mobilePattern: /^[5-9]\d{8}$/ },
  'ES': { code: '+34', localPrefix: '', name: 'Spanien', mobilePattern: /^[67]\d{8}$/ },
  'UK': { code: '+44', localPrefix: '0', name: 'Großbritannien', mobilePattern: /^7\d{9}$/ },
};

// Default country for detection
const DEFAULT_COUNTRY = 'DE';

/**
 * Remove all non-digit characters except leading +
 */
export const cleanPhoneNumber = (phone) => {
  if (!phone) return '';
  const cleaned = phone.replace(/[^\d+]/g, '');
  // Keep only one + at the start
  if (cleaned.startsWith('+')) {
    return '+' + cleaned.slice(1).replace(/\+/g, '');
  }
  return cleaned;
};

/**
 * Detect if a phone number is in local format and which country it might be from
 */
export const detectLocalFormat = (phone) => {
  const cleaned = cleanPhoneNumber(phone);
  
  // Already in international format
  if (cleaned.startsWith('+')) {
    return { isLocal: false, phone: cleaned };
  }
  
  // Check for German local format (most common case)
  // Starts with 0 followed by mobile prefix (15, 16, 17)
  if (cleaned.startsWith('0')) {
    const withoutLeadingZero = cleaned.slice(1);
    
    // Check each country
    for (const [countryCode, config] of Object.entries(COUNTRY_CODES)) {
      if (config.localPrefix === '0' && config.mobilePattern.test(withoutLeadingZero)) {
        return {
          isLocal: true,
          detectedCountry: countryCode,
          localNumber: cleaned,
          suggestedInternational: config.code + withoutLeadingZero,
          countryName: config.name
        };
      }
    }
    
    // Default to Germany if starts with 0 and looks like mobile
    if (/^0(15|16|17)\d{8,9}$/.test(cleaned)) {
      return {
        isLocal: true,
        detectedCountry: 'DE',
        localNumber: cleaned,
        suggestedInternational: '+49' + cleaned.slice(1),
        countryName: 'Deutschland'
      };
    }
  }
  
  // Check for numbers without leading 0 (might be missing country code)
  // Try to match German mobile patterns
  if (/^(15|16|17)\d{8,9}$/.test(cleaned)) {
    return {
      isLocal: true,
      detectedCountry: 'DE',
      localNumber: cleaned,
      suggestedInternational: '+49' + cleaned,
      countryName: 'Deutschland'
    };
  }
  
  // Can't detect - might be invalid
  return {
    isLocal: false,
    isInvalid: true,
    phone: cleaned,
    error: 'Unbekanntes Format'
  };
};

/**
 * Validate if a phone number is in correct international format
 */
export const validateInternationalFormat = (phone) => {
  const cleaned = cleanPhoneNumber(phone);
  
  if (!cleaned.startsWith('+')) {
    return { valid: false, error: 'Muss mit + beginnen' };
  }
  
  // Check minimum length (+ country code + number)
  if (cleaned.length < 10) {
    return { valid: false, error: 'Nummer zu kurz' };
  }
  
  if (cleaned.length > 16) {
    return { valid: false, error: 'Nummer zu lang' };
  }
  
  // Check if it matches any known country pattern
  for (const [countryCode, config] of Object.entries(COUNTRY_CODES)) {
    if (cleaned.startsWith(config.code)) {
      const nationalPart = cleaned.slice(config.code.length);
      if (config.mobilePattern.test(nationalPart)) {
        return { 
          valid: true, 
          country: countryCode, 
          countryName: config.name,
          formatted: cleaned
        };
      }
    }
  }
  
  // Accept if it looks like a valid international number even if country not recognized
  if (/^\+\d{10,15}$/.test(cleaned)) {
    return { valid: true, formatted: cleaned };
  }
  
  return { valid: false, error: 'Ungültiges Format' };
};

/**
 * Format phone number for display
 */
export const formatPhoneForDisplay = (phone) => {
  const cleaned = cleanPhoneNumber(phone);
  
  if (!cleaned.startsWith('+')) {
    return cleaned;
  }
  
  // German format: +49 151 12345678
  if (cleaned.startsWith('+49')) {
    const national = cleaned.slice(3);
    if (national.length >= 10) {
      return `+49 ${national.slice(0, 3)} ${national.slice(3, 7)} ${national.slice(7)}`;
    }
    return `+49 ${national}`;
  }
  
  // Generic format: +XX XXX XXXXXXXX
  if (cleaned.length > 5) {
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3, 6)} ${cleaned.slice(6)}`;
  }
  
  return cleaned;
};

/**
 * Normalize phone number to international format
 * Returns null if invalid
 */
export const normalizeToInternational = (phone, defaultCountry = DEFAULT_COUNTRY) => {
  const cleaned = cleanPhoneNumber(phone);
  
  // Already international
  if (cleaned.startsWith('+')) {
    const validation = validateInternationalFormat(cleaned);
    return validation.valid ? cleaned : null;
  }
  
  // Try to detect and convert
  const detection = detectLocalFormat(phone);
  if (detection.suggestedInternational) {
    return detection.suggestedInternational;
  }
  
  // Try default country
  const countryConfig = COUNTRY_CODES[defaultCountry];
  if (countryConfig) {
    let normalized = cleaned;
    if (countryConfig.localPrefix && cleaned.startsWith(countryConfig.localPrefix)) {
      normalized = cleaned.slice(countryConfig.localPrefix.length);
    }
    const candidate = countryConfig.code + normalized;
    const validation = validateInternationalFormat(candidate);
    if (validation.valid) {
      return candidate;
    }
  }
  
  return null;
};

/**
 * Main validation function - returns validation result with suggestions
 */
export const validatePhoneNumber = (phone) => {
  if (!phone || phone.trim() === '') {
    return {
      valid: false,
      error: 'Bitte Telefonnummer eingeben'
    };
  }
  
  const cleaned = cleanPhoneNumber(phone);
  
  // Check if already valid international format
  if (cleaned.startsWith('+')) {
    const validation = validateInternationalFormat(cleaned);
    if (validation.valid) {
      return {
        valid: true,
        normalized: cleaned,
        display: formatPhoneForDisplay(cleaned),
        country: validation.countryName
      };
    }
    return {
      valid: false,
      error: validation.error || 'Ungültige internationale Nummer'
    };
  }
  
  // Check for local format
  const detection = detectLocalFormat(phone);
  
  if (detection.isLocal && detection.suggestedInternational) {
    return {
      valid: false,
      needsConfirmation: true,
      localNumber: detection.localNumber,
      suggestedInternational: detection.suggestedInternational,
      countryName: detection.countryName,
      message: `Meinten Sie die ${detection.countryName.toLowerCase()}e Handynummer ${formatPhoneForDisplay(detection.suggestedInternational)}?`
    };
  }
  
  if (detection.isInvalid) {
    return {
      valid: false,
      error: 'Bitte Nummer im internationalen Format eingeben (z.B. +49 151 12345678)'
    };
  }
  
  return {
    valid: false,
    error: 'Ungültiges Telefonnummernformat'
  };
};

export default {
  cleanPhoneNumber,
  detectLocalFormat,
  validateInternationalFormat,
  formatPhoneForDisplay,
  normalizeToInternational,
  validatePhoneNumber,
  COUNTRY_CODES
};
